import { useState } from 'react'

import { palette, polygonArea, ru, type Точка } from '../../lib/physics'
import { Choice, Predict } from './common'

/**
 * Палетка (урок 5): фигура на клетчатой сетке. Полные клетки — одним цветом, неполные —
 * другим; площадь S = S₁·(N + K/2). Для треугольника палетка сверяется с формулой,
 * для кляксы формулы нет — только палетка.
 */

const PX = 56 // пикселей на сантиметр
const X0 = 20
const Y0 = 14

const ФИГУРЫ: { имя: string; точки: Точка[]; формула: string }[] = [
  { имя: 'треугольник', точки: [[0, 3], [4.5, 3], [1.5, 0]], формула: 'S = a·h / 2 = 4,5 · 3 / 2' },
  {
    имя: 'клякса',
    точки: [[0.4, 1.2], [1.2, 0.3], [2.4, 0.6], [3.4, 0.1], [4.6, 0.9], [4.2, 2.0], [5.0, 2.8], [3.6, 3.1], [2.6, 2.4], [1.4, 3.2], [0.3, 2.4]],
    формула: 'формулы нет',
  },
]
const КЛЕТКИ = [1, 0.5, 0.25] as const

export default function PaletteLab() {
  const [фиг, setФиг] = useState(0)
  const [клетка, setКлетка] = useState<number>(0.5)
  const [показать, setПоказать] = useState(false)

  const ф = ФИГУРЫ[фиг]
  const { N, K, S, клетки } = palette(ф.точки, клетка)
  const точно = polygonArea(ф.точки)
  const s1 = клетка * клетка
  const w = 5.2
  const h = 3.4

  return (
    <div className="card">
      <svg className="lab-svg" viewBox="0 0 330 210" role="img" aria-label="Фигура на палетке">
        {показать &&
          клетки.map((к) => (
            <rect key={`${к.x}:${к.y}`} x={X0 + к.x * PX} y={Y0 + к.y * PX} width={клетка * PX} height={клетка * PX} className={к.полная ? 'fill-unit-a' : 'fill-unit-b'} />
          ))}
        {Array.from({ length: Math.round(w / клетка) + 1 }, (_, i) => (
          <line key={`v${i}`} x1={X0 + i * клетка * PX} y1={Y0} x2={X0 + i * клетка * PX} y2={Y0 + h * PX} className="ink-muted" strokeWidth={0.5} />
        ))}
        {Array.from({ length: Math.round(h / клетка) + 1 }, (_, i) => (
          <line key={`h${i}`} x1={X0} y1={Y0 + i * клетка * PX} x2={X0 + w * PX} y2={Y0 + i * клетка * PX} className="ink-muted" strokeWidth={0.5} />
        ))}
        <polygon points={ф.точки.map(([x, y]) => `${X0 + x * PX},${Y0 + y * PX}`).join(' ')} className="ink-heavy" strokeWidth={2.5} fill="none" />
      </svg>

      {показать ? (
        <div className="lab-readout">
          <span className="el">полных клеток N = {N}</span>
          <span className="band">неполных клеток K = {K}</span>
          <span>S₁ = {ru(клетка, 2)} см · {ru(клетка, 2)} см = {ru(s1, 4)} см²</span>
          <span>S = S₁·(N + K/2) = <b>{ru(S, 2)} см²</b></span>
          <span>{ф.формула === 'формулы нет' ? 'по формуле — нельзя' : `${ф.формула} = ${ru(точно, 2)} см²`}</span>
        </div>
      ) : (
        <div className="lab-caption">Сначала прикинь сам, потом нажми «Посчитать клетки».</div>
      )}

      <div className="row-wrap" style={{ marginTop: 10 }}>
        <button className="btn btn-primary" onClick={() => setПоказать(true)}>🔲 Посчитать клетки</button>
      </div>

      <Predict
        question="Внутри фигуры 21 полная клетка и 12 неполных, клетка 0,25 см². Какова площадь?"
        options={['5,25 см²', '6,75 см²', '8,25 см²']}
        right={1}
        revealed={показать}
        explain="Неполные клетки в среднем закрыты наполовину: 0,25 см² · (21 + 12/2) = 0,25 · 27 = 6,75 см². В уроке так же вышло и по формуле треугольника."
      />

      <div className="lab-sub">Фигура</div>
      <Choice options={ФИГУРЫ.map((f, i) => ({ value: i, label: f.имя }))} value={фиг} onChange={(v) => { setФиг(v); setПоказать(false) }} />
      <div className="lab-sub">Сторона клетки</div>
      <Choice options={КЛЕТКИ.map((c) => ({ value: c, label: `${ru(c, 2)} см` }))} value={клетка} onChange={(v) => { setКлетка(v); setПоказать(false) }} />
      <p className="muted small">
        Чем мельче клетка, тем ближе палетка к точной площади. Площадь кляксы формулой не посчитать —
        её можно только измерить.
      </p>
    </div>
  )
}
